import {ProviderLogo} from '@github-ui/copilot-byok/providers/ProviderLogo'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {sendEvent} from '@github-ui/hydro-analytics'
import {Tooltip} from '@github-ui/pacer/Tooltip'
import {useLocation} from '@github-ui/react-router'
import {CopilotIcon, SyncIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, ButtonGroup, ConfirmationDialog, IconButton, Link, VisuallyHidden} from '@primer/react'
import {clsx} from 'clsx'
import React, {type ReactNode, useCallback, useEffect, useRef, useState} from 'react'

import {useOverlayDirection} from '../contexts/OverlayDirectionContext'
import {formatQuotaResetLabel} from '../hooks/use-quota-banner'
import {AUTO_MODEL_ID, getAutoModel, isAutoModeForFreeEdu} from '../utils/auto-model'
import {
  ALMOST_OUT_THRESHOLD,
  APPROACHING_THRESHOLD,
  ENABLE_ADDITIONAL_REQUESTS_URL,
  FREE_QUOTA_TRIGGER,
  MANAGE_BILLING_URL,
  MANAGE_BUDGET_URL,
  PREMIUM_QUOTA_TRIGGER,
  TOKEN_BASED_BILLING_MODEL_PRICING_DETAILS_URL,
  UPGRADE_PLAN_URL,
  UPGRADE_PLAN_URL_NEW,
} from '../utils/copilot-chat-helpers'
import {getActiveMessages} from '../utils/copilot-chat-subthreading-helpers'
import {
  type CopilotChatMessage,
  type CopilotChatMode,
  type CopilotChatModel,
  type CopilotChatReference,
  CopilotLicenseType,
  CopilotPlan,
} from '../utils/copilot-chat-types'
import {copilotFeatureFlags} from '../utils/copilot-feature-flags'
import {copilotLocalStorage} from '../utils/copilot-local-storage'
// eslint-disable-next-line no-restricted-imports
import {useChatState} from '../utils/CopilotChatContext'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import {
  categorizeModels,
  type CustomModelGroup,
  getMinimumRequiredPlan,
  hasModelCategoryDiversity,
} from '../utils/model-picker-helpers'
import {FIRST_PARTY_VENDORS} from '../utils/model-vendors'
import {BASE_MODEL_ID, getDefaultModel, isCustomModel} from '../utils/models'
import {RECOMMENDED_MODEL_IDS} from '../utils/recommended-chat-models'
import styles from './ModelPicker.module.css'
import {COPILOT_CHAT_MENU_PORTAL_ROOT} from './PortalContainerUtils'
import {useEntitlement} from './quota/EntitlementContext'

/**
 * Formats a billing multiplier for display.
 * @param multiplier - The billing multiplier value
 * @returns A formatted string like "3x", "1.5x", "0.33x", or "0x" (default)
 */
function formatMultiplier(multiplier: number | undefined): string {
  // Default to 0 if no multiplier is defined
  const value = multiplier ?? 0

  // Convert to number to remove trailing zeros, then back to string
  return `${Number(value.toFixed(2))}x`
}

type ModelPickerType = 'global' | 'message-retry'
type Command = NewConversationDueToExternalContent | NewConversationDueToDegradedImageSupport

// This isn't 100% typesafe, but if this pattern continues to extend considering separating out command types into the shape of their actions and reasons as their own enums.
type NewConversationDueToExternalContent = {
  action: 'requires-new-conversation'
  reason: 'external-content'
}
type NewConversationDueToDegradedImageSupport = {
  action: 'requires-new-conversation'
  reason: 'degraded-image-support'
}

interface ModelPickerProps {
  onNewThreadSelected: () => Promise<void>
  type?: ModelPickerType
  selectedModel?: CopilotChatModel // defaults to the thread's model if unset
  onModelSelected?: (model: CopilotChatModel) => void
  disabled?: boolean
}

export function ModelPicker({
  onNewThreadSelected,
  type = 'global',
  selectedModel,
  onModelSelected,
  disabled = false,
}: ModelPickerProps) {
  const manager = useChatManager()

  const {
    model,
    availableModels,
    mode,
    messages,
    currentReferences,
    selectedThreadID,
    modelsLoading,
    isWaitingOnCopilot,
    streamingMessage,
  } = useChatState()
  const {premiumInteractionsQuotaExceeded, overagesEnabled, plan, licenseType, tokenBasedBillingEnabled} =
    useEntitlement()
  const modelsPending = modelsLoading.state === 'pending'
  const {search, pathname} = useLocation()
  const hasSelectedDefaultModel = useRef(false)

  const shouldShowFallbackModel =
    copilotFeatureFlags.premiumRequestQuotasEnabled && premiumInteractionsQuotaExceeded && !overagesEnabled

  let isDisabled = disabled
  // Check FF for disabling model picker while streaming
  if (copilotFeatureFlags.disableModelPickerWhileStreaming) {
    // Disable model picker when streaming is active
    const isStreaming = isWaitingOnCopilot || streamingMessage !== null
    isDisabled = disabled || isStreaming
  }

  const [pending, setPending] = useState<{
    model: CopilotChatModel
    command: Command
  } | null>(null)

  const modelsLoadedRef = useRef(false)

  if (!selectedModel) {
    // eslint-disable-next-line react-hooks/immutability
    selectedModel = model
  }

  useEffect(() => {
    if (modelsPending && licenseType !== CopilotLicenseType.Unlicensed) {
      void manager.fetchModels()
    }
  }, [manager, modelsPending, licenseType])

  useEffect(() => {
    // After models have loaded, select the right model. This is either (in order):
    // 1. the model last used for the thread, stored in local storage
    // 2. the default model if no model is stored in local storage
    // 3. the fallback model if none of the prior models are available because
    //    - they are premium models _and_
    //    - the user has exhausted their premium consumption limits _and_
    //    - the user has no additional premium request billing budget enabled
    if (!hasSelectedDefaultModel.current && !modelsPending && availableModels.length > 1) {
      const storedModel = copilotLocalStorage.getModel(selectedThreadID)
      const storedModelId = storedModel?.id

      // when the legacy frontend curation flag is on, treat models outside the
      // hardcoded curated list as retired so old threads recover to the default.
      // The new backend-curated flow (copilot_chat_models_via_backend) returns
      // an already-curated list, so skip this filter when that flag is on —
      // otherwise the frontend's stale hardcoded set would drag.
      const candidateModels =
        copilotFeatureFlags.recommendedChatModelsOnly && !copilotFeatureFlags.modelsViaBackend
          ? availableModels.filter(m => RECOMMENDED_MODEL_IDS.has(m.id))
          : availableModels

      // check if the thread's stored model is available
      let modelChoice =
        candidateModels.find(
          m => m.id === storedModelId && (shouldShowFallbackModel || storedModelId !== BASE_MODEL_ID),
        ) || null
      if (
        // select the default model if the current model is not available
        (!modelChoice && plan) ||
        // Always force the Auto default for Free/EDU users (stored model may be stale)
        isAutoModeForFreeEdu(plan) ||
        (plan !== CopilotPlan.IndividualFree &&
          modelChoice?.billing?.is_premium &&
          premiumInteractionsQuotaExceeded &&
          !overagesEnabled)
      ) {
        modelChoice = getDefaultModel(candidateModels, plan, premiumInteractionsQuotaExceeded, overagesEnabled)
      }
      if (modelChoice) {
        const isNewThread = selectedThreadID === null
        manager.selectModel(modelChoice, true, isNewThread)
        hasSelectedDefaultModel.current = true
      }
    }
  }, [
    availableModels,
    manager,
    model,
    modelsPending,
    overagesEnabled,
    plan,
    premiumInteractionsQuotaExceeded,
    selectedThreadID,
    shouldShowFallbackModel,
  ])

  // Load model from URL if it exists
  useEffect(() => {
    if (mode !== 'immersive') return

    const urlSearchParams = new URLSearchParams(search)
    const queryModel = urlSearchParams.get('model')

    if (queryModel && (pathname === '/copilot' || pathname === '/copilot/')) {
      if (model.id === queryModel) return

      // Only run this logic when availableModels is populated for the first time
      if (modelsLoadedRef.current || !availableModels?.length) return

      modelsLoadedRef.current = true
      const availableModel = availableModels?.find(m => m.id === queryModel)
      if (availableModel) {
        void manager.selectModel(availableModel, false)
        sendEvent('dotcom_chat.activate', {target: 'COPILOT_MODEL_LOADED_FROM_URL', mode: 'immersive'})
      }
    }
  }, [availableModels, manager, mode, model?.id, pathname, search])

  function selectModel(m: CopilotChatModel): void {
    if (onModelSelected) {
      onModelSelected(m)
    } else {
      void manager.selectModel(m)
    }
  }

  // If you don't have at least two models, don't show the picker
  if (availableModels.length < 2) return null

  const onModelPicked = (newModel: CopilotChatModel) => {
    if (type === 'global' && isSameModel(selectedModel, newModel)) return

    sendEvent('dotcom_chat.activate', {
      target: 'MODEL_PICKER_MODEL_SELECT',
      mode: 'immersive',
      model: newModel.id,
      isUnchanged: isSameModel(selectedModel, newModel),
      type,
    })

    if (isSameModel(selectedModel, newModel)) {
      selectModel(newModel)
    } else if (
      modelSwitchingRequiresNewConversationDueToExternalContent(messages, newModel, type === 'message-retry')
    ) {
      setPending({model: newModel, command: {action: 'requires-new-conversation', reason: 'external-content'}})
    } else if (
      modelSwitchingRequiresNewConversationDueToDegradedImageSupport(
        messages,
        currentReferences,
        newModel,
        type === 'message-retry',
      )
    ) {
      setPending({model: newModel, command: {action: 'requires-new-conversation', reason: 'degraded-image-support'}})
    } else {
      selectModel(newModel)
    }
  }

  return (
    <>
      <ModelPickerDesign
        selectedModel={selectedModel}
        models={availableModels}
        type={type}
        onModelPicked={onModelPicked}
        disabled={isDisabled}
        variant={'invisible'}
        plan={plan ?? undefined}
        mode={mode}
        isTokenBasedBillingEnabled={tokenBasedBillingEnabled}
        recommendedOnly={copilotFeatureFlags.recommendedChatModelsOnly}
      />
      {pending?.command.action === 'requires-new-conversation' && (
        <NewConversationConfirmationDialog
          model={pending.model}
          reason={pending.command.reason}
          onDismiss={() => {
            sendEvent('dotcom_chat.activate', {
              target: 'MODEL_PICKER_DIALOG_REQUIRES_NEW_CONVERSATION_CANCEL',
              mode: 'immersive',
              model: pending.model.id,
              type,
            })
            setPending(null)
          }}
          onSwitch={async () => {
            sendEvent('dotcom_chat.activate', {
              target: 'MODEL_PICKER_DIALOG_REQUIRES_NEW_CONVERSATION_CONFIRM',
              mode: 'immersive',
              model: pending.model.id,
              type,
            })
            setPending(null)
            await onNewThreadSelected()
            void manager.selectModel(pending.model)
          }}
        />
      )}
    </>
  )
}

interface ModelPickerDesignProps {
  selectedModel: CopilotChatModel
  models: CopilotChatModel[]
  onModelPicked: (m: CopilotChatModel) => void
  type: ModelPickerType
  disabled: boolean
  variant?: 'default' | 'invisible'
  plan?: CopilotPlan
  showCategories?: boolean
  isTokenBasedBillingEnabled?: boolean
  showPreviewModelsSeparately?: boolean
  recommendedOnly?: boolean
  /** Chat mode for portal rendering - defaults to 'immersive' */
  mode?: CopilotChatMode
}

interface RetryButtonGroupProps {
  selectedModel: CopilotChatModel
  onModelPicked: (m: CopilotChatModel) => void
  disabled: boolean
  ref?: React.Ref<HTMLDivElement>
}

function RetryButtonGroup({ref, ...props}: RetryButtonGroupProps) {
  const {selectedModel, onModelPicked, disabled, ...buttonProps} = props
  return (
    <ButtonGroup className={styles.messageRetryButtonGroup} ref={ref}>
      <IconButton
        variant="invisible"
        aria-label={`Retry with ${selectedModel.displayName}`}
        onClick={() => onModelPicked(selectedModel)}
        icon={SyncIcon}
        disabled={disabled}
        className={styles.messageRetryButton}
      />
      <div className={styles.messageRetryDivider} />
      <IconButton
        variant="invisible"
        icon={TriangleDownIcon}
        aria-label="Retry with…"
        disabled={disabled}
        className={styles.messageRetryMenuButton}
        {...buttonProps}
      />
    </ButtonGroup>
  )
}

export function ModelPickerDesign({
  selectedModel,
  models,
  onModelPicked,
  type,
  disabled,
  variant = 'invisible',
  plan,
  showCategories,
  isTokenBasedBillingEnabled = false,
  showPreviewModelsSeparately = true,
  recommendedOnly = false,
  mode = 'immersive',
}: ModelPickerDesignProps) {
  const [isOpen, setIsOpen] = useState(false)
  const preferredOverlaySide = useOverlayDirection()

  // `isCuratedSource` is true when the model list comes from a curated source,
  // either the legacy frontend curation (recommendedChatModelsOnly) or the backend-curated endpoint (modelsViaBackend).
  const isCuratedSource = recommendedOnly || copilotFeatureFlags.modelsViaBackend
  const effectiveRecommendedOnly = recommendedOnly && !copilotFeatureFlags.modelsViaBackend

  // For free plan users, combine API models with hardcoded upsell models.
  // API models win on duplicates. Skipped when any curated source is in
  // play, because the backend already returns upsell placeholders for free
  // users (and the legacy curated picker handles upsell rows itself); merging
  // the hardcoded list on top would double-up entries whose display names
  // drift.
  const modelsToUse: CopilotChatModel[] =
    plan === CopilotPlan.IndividualFree && !isCuratedSource
      ? (() => {
          // Get display names from API models to check for duplicates
          const apiModelDisplayNames = new Set(models.map(m => m.displayName))
          // Filter out upsell models that have the same display name as API models
          const filteredUpsellModels = upsellModelsForFreeUsers.filter(m => !apiModelDisplayNames.has(m.displayName))
          // Convert upsell models to CopilotChatModel format and combine
          const convertedUpsellModels = filteredUpsellModels.map(createMockModelFromUpsellModel)
          return [...models, ...convertedUpsellModels]
        })()
      : models

  // Any curated source is intentionally categorized, so force categories on.
  const shouldShowModelCategories =
    showCategories !== false &&
    (isCuratedSource || (copilotFeatureFlags.immersiveStructuredModelPicker && hasModelCategoryDiversity(modelsToUse)))

  // Categorize models using the utility function
  const modelTiers = categorizeModels(modelsToUse, {
    shouldShowCategories: shouldShowModelCategories,
    shouldShowPreviewModelsSeparately: showPreviewModelsSeparately,
    recommendedOnly: effectiveRecommendedOnly,
    userPlan: plan,
  })

  // Extract the categorized models for easier access
  const {
    lowTier: lowTierModels,
    midTier: midTierModels,
    highTier: highTierModels,
    base: baseModels,
    preview: previewModels,
    custom: customModelGroups,
  } = modelTiers

  // When Auto Mode is active for Free/EDU users, inject the Auto model as the only
  // selectable entry and lock all other models behind upsell.
  const autoModeActive = isAutoModeForFreeEdu(plan)
  // In stage 1, locked === active (Free/EDU always locked). Future stages may
  // allow autoModeActive without locking (e.g. paid users opting into Auto).
  const autoModeLocked = autoModeActive
  const autoModel = autoModeActive ? getAutoModel() : null
  const effectiveBaseModels = autoModel ? [autoModel, ...baseModels.filter(m => m.id !== AUTO_MODEL_ID)] : baseModels

  const hasCustomModels = Object.keys(customModelGroups).length > 0

  const handleMenuOpen = useCallback(
    (open: boolean) => {
      // In case the retry divider is clicked
      const shouldOpen = !disabled && open
      setIsOpen(shouldOpen)

      if (shouldOpen) {
        sendEvent('dotcom_chat.activate', {
          target: 'MODEL_PICKER_OPEN',
          mode: 'immersive',
          model: selectedModel.id,
          type,
        })
      }
    },
    [disabled, selectedModel.id, type],
  )

  const setVariant = variant ?? 'invisible'

  const componentRef = useRef<HTMLDivElement>(null)
  const [menuDirection, setMenuDirection] = useState<'up' | 'down'>('down')

  return (
    <div ref={componentRef}>
      <ActionMenu open={isOpen} onOpenChange={handleMenuOpen}>
        {type === 'global' ? (
          <ActionMenu.Button className={styles.menuButton} variant={setVariant} disabled={disabled}>
            <span className="sr-only">Model: </span>
            <div className={styles.buttonName}>{selectedModel.displayName}</div>
          </ActionMenu.Button>
        ) : (
          <ActionMenu.Anchor aria-label={'Retry with model'}>
            <RetryButtonGroup selectedModel={selectedModel} onModelPicked={onModelPicked} disabled={disabled} />
          </ActionMenu.Anchor>
        )}

        {/* eslint-disable-next-line primer-react/no-system-props */}
        <ActionMenu.Overlay
          width={type === 'global' ? 'medium' : 'small'}
          side={preferredOverlaySide}
          overflow="auto"
          className={styles.actionMenuOverlay}
          maxHeight="fit-content"
          align="end"
          position={mode === 'assistive' ? 'relative' : undefined}
          portalContainerName={mode === 'assistive' ? COPILOT_CHAT_MENU_PORTAL_ROOT : undefined}
          onPositionChange={({position}) => {
            const downPositions = ['inside-bottom', 'outside-bottom']
            setMenuDirection(downPositions.includes(position.anchorSide) ? 'down' : 'up')
          }}
        >
          <ActionList className={styles.actionList} selectionVariant={type === 'global' ? 'single' : undefined}>
            {type === 'message-retry' && menuDirection === 'down' && (
              <>
                <RetryListItem selectedModel={selectedModel} onModelPicked={onModelPicked} />
                <ActionList.Divider />
              </>
            )}
            {type === 'global' && <div className={styles.menuHeading}>Models</div>}
            {(copilotFeatureFlags.premiumRequestQuotasEnabled || isTokenBasedBillingEnabled) && (
              <ModelPickerHeader
                isTokenBasedBillingEnabled={isTokenBasedBillingEnabled}
                autoModeLocked={autoModeLocked}
              />
            )}

            {effectiveBaseModels.length > 0 && (
              <ActionList.Group>
                {type !== 'global' && <ActionList.GroupHeading>Models</ActionList.GroupHeading>}
                {effectiveBaseModels.map(m => (
                  <ModelListItem
                    key={m.id}
                    model={m}
                    selected={isSameModel(selectedModel, m) && type === 'global'}
                    onModelPicked={onModelPicked}
                    isTokenBasedBillingEnabled={isTokenBasedBillingEnabled}
                    autoModeLocked={autoModeLocked}
                  />
                ))}
                {(lowTierModels.length > 0 ||
                  midTierModels.length > 0 ||
                  highTierModels.length > 0 ||
                  previewModels.length > 0 ||
                  hasCustomModels) && <ActionList.Divider />}
              </ActionList.Group>
            )}

            {lowTierModels.length > 0 && (
              <ActionList.Group>
                <ActionList.GroupHeading>Fast and cost-efficient</ActionList.GroupHeading>

                {lowTierModels.map(m => (
                  <ModelListItem
                    key={m.id}
                    model={m}
                    selected={isSameModel(selectedModel, m) && type === 'global'}
                    onModelPicked={onModelPicked}
                    isTokenBasedBillingEnabled={isTokenBasedBillingEnabled}
                    autoModeLocked={autoModeLocked}
                  />
                ))}
                {(midTierModels.length > 0 || highTierModels.length > 0) && <ActionList.Divider />}
              </ActionList.Group>
            )}

            {midTierModels.length > 0 && (
              <ActionList.Group>
                <ActionList.GroupHeading>Versatile and highly intelligent</ActionList.GroupHeading>

                {midTierModels.map(m => (
                  <ModelListItem
                    key={m.id}
                    model={m}
                    selected={isSameModel(selectedModel, m) && type === 'global'}
                    onModelPicked={onModelPicked}
                    isTokenBasedBillingEnabled={isTokenBasedBillingEnabled}
                    autoModeLocked={autoModeLocked}
                  />
                ))}
                {(highTierModels.length > 0 || previewModels.length > 0 || hasCustomModels) && <ActionList.Divider />}
              </ActionList.Group>
            )}

            {highTierModels.length > 0 && (
              <ActionList.Group>
                <ActionList.GroupHeading>Most powerful at complex tasks</ActionList.GroupHeading>

                {highTierModels.map(m => (
                  <ModelListItem
                    key={m.id}
                    model={m}
                    selected={isSameModel(selectedModel, m) && type === 'global'}
                    onModelPicked={onModelPicked}
                    isTokenBasedBillingEnabled={isTokenBasedBillingEnabled}
                    autoModeLocked={autoModeLocked}
                  />
                ))}
                {(previewModels.length > 0 || hasCustomModels) && <ActionList.Divider />}
              </ActionList.Group>
            )}

            {previewModels.length > 0 && (
              <ActionList.Group>
                <ActionList.GroupHeading>Preview</ActionList.GroupHeading>

                {previewModels.map(m => (
                  <ModelListItem
                    key={m.id}
                    model={m}
                    selected={isSameModel(selectedModel, m) && type === 'global'}
                    onModelPicked={onModelPicked}
                    isTokenBasedBillingEnabled={isTokenBasedBillingEnabled}
                    autoModeLocked={autoModeLocked}
                  />
                ))}
                {hasCustomModels && <ActionList.Divider />}
              </ActionList.Group>
            )}

            {hasCustomModels && (
              <CustomModelActionListGroups customModelGroups={customModelGroups}>
                {m => (
                  <ModelListItem
                    key={m.id}
                    model={m}
                    selected={isSameModel(selectedModel, m) && type === 'global'}
                    onModelPicked={onModelPicked}
                    isTokenBasedBillingEnabled={isTokenBasedBillingEnabled}
                    autoModeLocked={autoModeLocked}
                  />
                )}
              </CustomModelActionListGroups>
            )}

            {type === 'message-retry' && menuDirection === 'up' && (
              <>
                <ActionList.Divider />
                <RetryListItem selectedModel={selectedModel} onModelPicked={onModelPicked} />
              </>
            )}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </div>
  )
}

function CustomModelActionListGroups({
  customModelGroups,
  children,
}: {
  customModelGroups: {[groupKey: string]: CustomModelGroup}
  children: (model: CopilotChatModel) => ReactNode
}) {
  const groupKeys = Object.keys(customModelGroups)
  const lastGroupIndex = groupKeys.length - 1

  return groupKeys.map((groupKey, i) => {
    const group = customModelGroups[groupKey]
    if (!group) return null
    return (
      <ActionList.Group key={groupKey}>
        <ActionList.GroupHeading>{group.displayName}</ActionList.GroupHeading>
        {group.models.map(children)}
        {i < lastGroupIndex && <ActionList.Divider />}
      </ActionList.Group>
    )
  })
}

interface RetryListItemProps {
  selectedModel: CopilotChatModel
  onModelPicked: (m: CopilotChatModel) => void
}

function RetryListItem({selectedModel, onModelPicked}: RetryListItemProps) {
  return (
    <ActionList.Item key={selectedModel.id} onSelect={() => onModelPicked(selectedModel)}>
      <ActionList.LeadingVisual>
        <SyncIcon />
      </ActionList.LeadingVisual>
      Try again
      <ActionList.Description variant="block" truncate>
        {selectedModel.displayName}
      </ActionList.Description>
    </ActionList.Item>
  )
}

interface ModelListItemProps {
  model: CopilotChatModel
  selected: boolean
  onModelPicked: (m: CopilotChatModel) => void
}

export function ModelListItem({
  model,
  selected,
  onModelPicked,
  isTokenBasedBillingEnabled = false,
  autoModeLocked = false,
}: ModelListItemProps & {isTokenBasedBillingEnabled?: boolean; autoModeLocked?: boolean}) {
  const {plan, premiumInteractionsQuotaExceeded, chatQuotaExceeded, overagesEnabled, canUpgradePlan} = useEntitlement()
  const freePlanAndOutOfQuota = plan === CopilotPlan.IndividualFree && chatQuotaExceeded
  const premiumModelAndOutOfQuota =
    model.billing?.is_premium &&
    plan !== CopilotPlan.IndividualFree &&
    premiumInteractionsQuotaExceeded &&
    !overagesEnabled
  const modelOutOfPlan = plan && model.billing?.restricted_to && !model.billing?.restricted_to.includes(plan)
  const isUpsellModel = model.is_upsell === true || ('upsellModel' in model && model.upsellModel === true)
  const minRequiredPlan = getMinimumRequiredPlan(model)
  const isProPlusModel = minRequiredPlan === CopilotPlan.IndividualProPlus
  const isMaxOnlyModel = minRequiredPlan === CopilotPlan.IndividualMax && copilotFeatureFlags.maxUpsellEnabled
  const locked =
    freePlanAndOutOfQuota ||
    premiumModelAndOutOfQuota ||
    modelOutOfPlan ||
    isUpsellModel ||
    (autoModeLocked && model.id !== AUTO_MODEL_ID)
  const showLockedExplanation =
    locked && (copilotFeatureFlags.recommendedChatModelsOnly || copilotFeatureFlags.modelsViaBackend || autoModeLocked)

  const lockedTooltipText = showLockedExplanation
    ? getLockedModelTooltip({
        isUpsellOrOutOfPlan: !!(isUpsellModel || modelOutOfPlan),
        freePlanAndOutOfQuota: !!freePlanAndOutOfQuota,
        premiumModelAndOutOfQuota: !!premiumModelAndOutOfQuota,
        isProPlusOnly: isProPlusModel,
        isMaxOnly: isMaxOnlyModel,
        canUpgradePlan,
        autoModeLocked: !!(autoModeLocked && model.id !== AUTO_MODEL_ID),
      })
    : null

  // Render Multiplier when in Premium Request Unit (Auto pseudo-model has no real billing multiplier)
  const showMultiplierIndicators =
    model.id !== AUTO_MODEL_ID && !isCustomModel(model) && !isTokenBasedBillingEnabled && !isUpsellModel && !locked
  const multiplierLabelText = showMultiplierIndicators ? formatMultiplier(model.billing?.multiplier) : null

  const item = (
    <ActionList.Item
      key={model.id}
      onSelect={() => onModelPicked(model)}
      selected={selected}
      disabled={!!locked}
      aria-description={lockedTooltipText ? `Model locked. ${lockedTooltipText}` : undefined}
    >
      <ActionList.LeadingVisual>
        <ModelVendorIcon model={model} />
      </ActionList.LeadingVisual>
      <span className={clsx(locked && styles.modelNameDisabled)}>
        {model.displayName}
        {model.vendor && <VisuallyHidden>, {model.vendor} provider</VisuallyHidden>}
      </span>
      {showLockedExplanation && isMaxOnlyModel && <span className={styles.maxBadge}>Max</span>}
      {showLockedExplanation && isProPlusModel && <span className={styles.proPlusBadge}>Pro+</span>}
      {copilotFeatureFlags.premiumRequestQuotasEnabled && model.preview && model.id !== BASE_MODEL_ID && (
        <span className={styles.modelMetaLabel}>Preview</span>
      )}
      {showMultiplierIndicators && (
        <ActionList.TrailingVisual>
          <span className={styles.multiplier}>{multiplierLabelText}</span>
        </ActionList.TrailingVisual>
      )}
    </ActionList.Item>
  )

  if (lockedTooltipText) {
    return (
      <Tooltip text={lockedTooltipText} position="top">
        {item}
      </Tooltip>
    )
  }
  return item
}

function getLockedModelTooltip({
  isUpsellOrOutOfPlan,
  freePlanAndOutOfQuota,
  premiumModelAndOutOfQuota,
  isProPlusOnly,
  isMaxOnly,
  canUpgradePlan,
  autoModeLocked = false,
}: {
  isUpsellOrOutOfPlan: boolean
  freePlanAndOutOfQuota: boolean
  premiumModelAndOutOfQuota: boolean
  isProPlusOnly: boolean
  isMaxOnly: boolean
  canUpgradePlan: boolean
  autoModeLocked?: boolean
}): string {
  if (autoModeLocked)
    return isProPlusOnly
      ? 'Upgrade to Copilot Pro+ to select individual models'
      : 'Upgrade to Copilot Pro to select individual models'
  if (freePlanAndOutOfQuota) return "You've used all free requests this month."
  if (premiumModelAndOutOfQuota) return "You've reached your premium request limit this month."
  const planName = isMaxOnly
    ? 'Copilot Max'
    : isProPlusOnly
      ? 'Copilot Pro+'
      : isUpsellOrOutOfPlan
        ? 'Copilot Pro'
        : null
  if (!planName) return 'This model is not available on your plan.'
  return canUpgradePlan ? `Upgrade to ${planName} to access this model` : `This model requires ${planName}`
}

function NewConversationConfirmationDialog({
  model,
  onDismiss,
  onSwitch,
  reason,
}: {
  model: CopilotChatModel
  onDismiss: () => void
  onSwitch: () => void
  reason: string
}) {
  return (
    <ConfirmationDialog
      title="Switch model"
      onClose={gesture => (gesture === 'confirm' ? onSwitch() : onDismiss())}
      confirmButtonContent="New conversation"
      confirmButtonType="primary"
    >
      {reason === 'external-content' && (
        <p>
          The {model.displayName} model currently doesn’t support external context from ongoing conversations. Start a
          new conversation to chat with this model.
        </p>
      )}
      {reason === 'degraded-image-support' && (
        <p>
          The {model.displayName} model doesn’t support answering questions about images. Start a new conversation
          without images to chat with this model.
        </p>
      )}
    </ConfirmationDialog>
  )
}

function ModelVendorIcon({model}: {model: CopilotChatModel}) {
  if (isCustomModel(model) && model.custom_model.provider) {
    return <ProviderLogo provider={model.custom_model.provider} />
  }

  return model.logoURL ? (
    <GitHubAvatar alt={`${model.vendor} logo`} square size={18} src={model.logoURL} />
  ) : (
    <CopilotIcon />
  )
}

function modelSwitchingRequiresNewConversationDueToExternalContent(
  messages: readonly CopilotChatMessage[],
  model: CopilotChatModel,
  onlyActiveMessages?: boolean,
): boolean {
  const messagesToCheck = onlyActiveMessages ? getActiveMessages(messages) : messages
  const hasFunctionCalls = messagesToCheck.some(message => message.skillExecutions?.length)

  return (
    hasFunctionCalls &&
    (!model.capabilities?.supports?.tool_calls ||
      model.isThirdParty ||
      // o1-ga supports tool calling, but CAPI gates it behind the
      // copilot_api_tools_for_non_streaming_models feature flag which is not yet fully rolled out.
      // Keep this guard until that flag is at 100%.
      model.capabilities.family === 'o1-ga')
  )
}

function modelSwitchingRequiresNewConversationDueToDegradedImageSupport(
  messages: readonly CopilotChatMessage[],
  currentReferences: CopilotChatReference[],
  model: CopilotChatModel,
  onlyActiveMessages?: boolean,
): boolean {
  const messagesToCheck = onlyActiveMessages ? getActiveMessages(messages) : messages
  const hasMediaContent =
    messagesToCheck.some(message => message.mediaContent?.length) || currentReferences.some(cr => cr.type === 'image')

  return hasMediaContent && !model.capabilities?.supports?.vision
}

function isSameModel(oldModel: CopilotChatModel, newModel: CopilotChatModel): boolean {
  return oldModel.id === newModel.id
}

type UpsellModel = Pick<CopilotChatModel, 'logoURL' | 'displayName' | 'id' | 'model_picker_category'> & {
  upsellModel: boolean
  billing: {multiplier: number}
  preview?: boolean
}

type UpsellModelList = UpsellModel[]

// Helper function to create a mock CopilotChatModel from UpsellModel for type compatibility
function createMockModelFromUpsellModel(upsellModel: UpsellModel): CopilotChatModel {
  const vendor = upsellModel.logoURL?.includes('anthropic')
    ? 'Anthropic'
    : upsellModel.logoURL?.includes('openai')
      ? 'Azure OpenAI'
      : upsellModel.logoURL?.includes('gemini')
        ? 'Google'
        : upsellModel.logoURL?.includes('x-ai')
          ? 'xAI'
          : 'Unknown'

  return {
    ...upsellModel,
    name: upsellModel.displayName,
    version: '1.0',
    vendor,
    // eslint-disable-next-line camelcase
    model_picker_enabled: true,
    hasLimitedCapabilities: false,
    isThirdParty: !FIRST_PARTY_VENDORS.has(vendor),
    capabilities: {
      family: 'unknown',
      limits: {
        // eslint-disable-next-line camelcase
        max_prompt_tokens: 4096,
      },
      supports: {},
      tokenizer: 'gpt',
      type: 'chat' as const,
    },
    preview: upsellModel.preview ?? false,
    billing: {
      ...upsellModel.billing,
      // eslint-disable-next-line camelcase
      is_premium: true,
    },
  } as CopilotChatModel
}

/**
 * Hardcoded list of upsell models displayed to free plan users in the model picker.
 *
 * **Why this list exists:**
 * The `/models` endpoint only returns models that are accessible to a given user based on their
 * entitlements. We do not currently have a way to receive all available models from CAPI,
 * including premium/upsell models that free users cannot access but should see as upgrade options.
 *
 * **Why we can't easily change this in CAPI:**
 * The `/models` endpoint serves various integrators (IDE extensions, CLI, etc.) who rely on it
 * returning only actionable models. Changing this behavior would require coordinated updates
 * across all integrators and could break existing functionality.
 *
 * @see {@link createMockModelFromUpsellModel} for how these are converted to CopilotChatModel
 */
const upsellModelsForFreeUsers: UpsellModelList = [
  // Lightweight category
  {
    id: 'grok-code-upgrade',
    displayName: 'Grok Code Fast 1',
    logoURL: '/images/modules/marketplace/models/families/xai.svg',
    upsellModel: true,
    // eslint-disable-next-line camelcase
    model_picker_category: 'lightweight',
    billing: {
      multiplier: 0.33,
    },
  },
  {
    id: 'gemini-3-flash-upgrade',
    displayName: 'Gemini 3 Flash',
    logoURL: '/images/modules/marketplace/models/families/gemini.svg',
    upsellModel: true,
    preview: true,
    // eslint-disable-next-line camelcase
    model_picker_category: 'lightweight',
    billing: {
      multiplier: 0.33,
    },
  },
  // Versatile category
  {
    id: 'gpt-5-upgrade',
    displayName: 'GPT-5',
    logoURL: '/images/modules/marketplace/models/families/openai-logo.svg',
    upsellModel: true,
    // eslint-disable-next-line camelcase
    model_picker_category: 'versatile',
    billing: {
      multiplier: 1,
    },
  },
  {
    id: 'gpt-5.1-upgrade',
    displayName: 'GPT-5.1',
    logoURL: '/images/modules/marketplace/models/families/openai-logo.svg',
    upsellModel: true,
    // eslint-disable-next-line camelcase
    model_picker_category: 'versatile',
    billing: {
      multiplier: 1,
    },
  },
  {
    id: 'gpt-5.2-upgrade',
    displayName: 'GPT-5.2',
    logoURL: '/images/modules/marketplace/models/families/openai-logo.svg',
    upsellModel: true,
    // eslint-disable-next-line camelcase
    model_picker_category: 'versatile',
    billing: {
      multiplier: 1,
    },
  },
  {
    id: 'claude-sonnet-4-upgrade',
    displayName: 'Claude Sonnet 4',
    logoURL: '/images/modules/marketplace/models/families/anthropic.svg',
    upsellModel: true,
    // eslint-disable-next-line camelcase
    model_picker_category: 'versatile',
    billing: {
      multiplier: 1,
    },
  },
  {
    id: 'claude-sonnet-4.5-upgrade',
    displayName: 'Claude Sonnet 4.5',
    logoURL: '/images/modules/marketplace/models/families/anthropic.svg',
    upsellModel: true,
    // eslint-disable-next-line camelcase
    model_picker_category: 'versatile',
    billing: {
      multiplier: 1,
    },
  },
  // Powerful category
  {
    id: 'claude-opus-4.5-upgrade',
    displayName: 'Claude Opus 4.5',
    logoURL: '/images/modules/marketplace/models/families/anthropic.svg',
    upsellModel: true,
    // eslint-disable-next-line camelcase
    model_picker_category: 'powerful',
    billing: {
      multiplier: 10,
    },
  },
  {
    id: 'gemini-3-pro-upgrade',
    displayName: 'Gemini 3 Pro',
    logoURL: '/images/modules/marketplace/models/families/gemini.svg',
    upsellModel: true,
    // eslint-disable-next-line camelcase
    model_picker_category: 'powerful',
    billing: {
      multiplier: 10,
    },
    preview: true,
  },
]

type EntitlementSnapshot = ReturnType<typeof useEntitlement>

function ModelPickerHeader({
  isTokenBasedBillingEnabled = false,
  autoModeLocked = false,
}: {
  isTokenBasedBillingEnabled?: boolean
  autoModeLocked?: boolean
}) {
  const entitlement = useEntitlement()

  const content =
    isTokenBasedBillingEnabled || autoModeLocked
      ? renderUbbContent(entitlement, autoModeLocked)
      : renderLegacyContent(entitlement)

  return content ? <div className={styles.header}>{content}</div> : null
}

// UBB users see a per-SKU quota message when one applies. When no scenario
// applies, fall back to a pricing-details link only when the picker is
// showing the full model list (curated modes — legacy frontend or backend —
// hide pricing because the user can't choose among them).
function renderUbbContent(entitlement: EntitlementSnapshot, autoModeLocked = false): ReactNode {
  const scenarioContent = renderUbbScenario(
    detectQuotaScenario(entitlement, UBB_THRESHOLDS),
    entitlement,
    autoModeLocked,
  )
  if (scenarioContent) return scenarioContent
  if (autoModeLocked) {
    return (
      <>
        <UpgradeLink url={UPGRADE_PLAN_URL_NEW} /> to access more models and higher limits.
      </>
    )
  }
  const isCuratedPicker = copilotFeatureFlags.recommendedChatModelsOnly || copilotFeatureFlags.modelsViaBackend
  return isCuratedPicker ? null : <PricingDetailsFallback />
}

function renderLegacyContent(entitlement: EntitlementSnapshot): ReactNode {
  return renderLegacyScenario(detectQuotaScenario(entitlement, LEGACY_THRESHOLDS), entitlement)
}

// Shared JSX helpers so both UBB and legacy paths use the same link/ask widgets
// without re-stating `<Link inline href=...>label</Link>` on every branch.
const UpgradeLink = ({url = UPGRADE_PLAN_URL}: {url?: string}) => (
  <Link inline href={url}>
    Upgrade
  </Link>
)

const ManageBillingLink = ({url = MANAGE_BILLING_URL}: {url?: string}) => (
  <Link inline href={url}>
    Manage billing
  </Link>
)

const EnableAdditionalLink = ({label, url = ENABLE_ADDITIONAL_REQUESTS_URL}: {label: string; url?: string}) => (
  <Link inline href={url}>
    {label}
  </Link>
)

const AdminBold = ({label}: {label: string}) => <span className="font-weight-bold">{label} </span>

const PricingDetailsFallback = () => (
  <>
    <Link inline href={TOKEN_BASED_BILLING_MODEL_PRICING_DETAILS_URL}>
      See pricing details
    </Link>{' '}
    for models.
  </>
)

function formatUsagePercent(percentRemaining: number): number {
  // Mirror QuotaBanner: display the floor of the current usage percentage.
  return Math.floor(100 - percentRemaining)
}

// Centralised state machine shared by UBB and legacy renderers. Mapping plan
// + quota state to a single scenario keeps the control flow in one place and
// guarantees the two copy paths handle the same set of cases.
type QuotaScenario =
  | 'free-exceeded'
  | 'free-almost-out'
  | 'free-approaching'
  | 'free-default'
  | 'individual-overage-active'
  | 'individual-premium-exceeded'
  | 'individual-premium-approaching'
  | 'individual-default'
  | 'pro-plus-overage-active'
  | 'pro-plus-premium-exceeded'
  | 'pro-plus-premium-approaching'
  | 'max-overage-active'
  | 'max-premium-exceeded'
  | 'max-premium-approaching'
  | 'business-premium-exceeded-upgradeable'
  | 'business-premium-approaching-upgradeable'
  | 'business-premium-exceeded-locked'
  | 'business-premium-approaching-locked'
  | 'enterprise-premium-exceeded-purchasable'
  | 'enterprise-premium-exceeded-locked'
  | 'enterprise-premium-approaching-purchasable'
  | 'enterprise-premium-approaching-locked'

// Footer-level thresholds for the "approaching" and (optional) "almost-out"
// tiers. UBB uses the same values as `use-quota-banner.ts` so the picker
// footer and the banner trigger at identical quota levels; legacy uses the
// historical FREE_QUOTA_TRIGGER / PREMIUM_QUOTA_TRIGGER constants so its
// behaviour is unchanged from main.
interface QuotaThresholds {
  freeApproaching: number
  freeAlmostOut?: number
  premiumApproaching: number
}

const UBB_THRESHOLDS: QuotaThresholds = {
  freeApproaching: APPROACHING_THRESHOLD,
  freeAlmostOut: ALMOST_OUT_THRESHOLD,
  premiumApproaching: APPROACHING_THRESHOLD,
}

const LEGACY_THRESHOLDS: QuotaThresholds = {
  freeApproaching: FREE_QUOTA_TRIGGER,
  premiumApproaching: PREMIUM_QUOTA_TRIGGER,
}

function detectQuotaScenario(e: EntitlementSnapshot, thresholds: QuotaThresholds): QuotaScenario | null {
  const {
    plan,
    premiumChatQuotaRemaining,
    premiumInteractionsQuotaExceeded,
    chatQuotaRemaining,
    chatQuotaExceeded,
    canPurchaseAdditionalQuota,
    canUpgradePlan,
    overagesEnabled,
  } = e

  if (plan === CopilotPlan.IndividualFree) {
    if (chatQuotaExceeded) return 'free-exceeded'
    if (thresholds.freeAlmostOut !== undefined && chatQuotaRemaining <= thresholds.freeAlmostOut) {
      return 'free-almost-out'
    }
    if (chatQuotaRemaining <= thresholds.freeApproaching) return 'free-approaching'
    return 'free-default'
  }

  // checking student to maintain parity
  if (plan === CopilotPlan.IndividualPro || plan === CopilotPlan.IndividualStudent) {
    if (premiumInteractionsQuotaExceeded && overagesEnabled) return 'individual-overage-active'
    if (premiumInteractionsQuotaExceeded) return 'individual-premium-exceeded'
    if (premiumChatQuotaRemaining <= thresholds.premiumApproaching && !overagesEnabled) {
      return 'individual-premium-approaching'
    }
    return 'individual-default'
  }

  if (plan === CopilotPlan.IndividualProPlus) {
    if (premiumInteractionsQuotaExceeded && overagesEnabled) return 'pro-plus-overage-active'
    if (premiumInteractionsQuotaExceeded) return 'pro-plus-premium-exceeded'
    if (premiumChatQuotaRemaining <= thresholds.premiumApproaching && !overagesEnabled) {
      return 'pro-plus-premium-approaching'
    }
    return null
  }

  if (plan === CopilotPlan.IndividualMax) {
    if (premiumInteractionsQuotaExceeded && overagesEnabled) return 'max-overage-active'
    if (premiumInteractionsQuotaExceeded) return 'max-premium-exceeded'
    if (premiumChatQuotaRemaining <= thresholds.premiumApproaching && !overagesEnabled) {
      return 'max-premium-approaching'
    }
    return null
  }

  if (plan === CopilotPlan.Business) {
    if (overagesEnabled) return null
    if (premiumInteractionsQuotaExceeded) {
      return canUpgradePlan ? 'business-premium-exceeded-upgradeable' : 'business-premium-exceeded-locked'
    }
    if (premiumChatQuotaRemaining <= thresholds.premiumApproaching) {
      return canUpgradePlan ? 'business-premium-approaching-upgradeable' : 'business-premium-approaching-locked'
    }
    return null
  }

  if (plan === CopilotPlan.Enterprise) {
    if (overagesEnabled) return null
    if (premiumInteractionsQuotaExceeded) {
      return canPurchaseAdditionalQuota
        ? 'enterprise-premium-exceeded-purchasable'
        : 'enterprise-premium-exceeded-locked'
    }
    if (premiumChatQuotaRemaining <= thresholds.premiumApproaching) {
      return canPurchaseAdditionalQuota
        ? 'enterprise-premium-approaching-purchasable'
        : 'enterprise-premium-approaching-locked'
    }
    return null
  }

  return null
}

// For UBB scenario we don't need 'exceeded' cases as the model picker is disabled/unclickable
function renderUbbScenario(scenario: QuotaScenario | null, e: EntitlementSnapshot, autoModeLocked = false): ReactNode {
  const upgradeLinkUrl = UPGRADE_PLAN_URL_NEW
  const upgradeSuffix = autoModeLocked ? ' to access more models and higher limits.' : ' to increase your limit.'
  const enableAdditionalUrl = copilotFeatureFlags.ubbNewCtaDestinations
    ? ENABLE_ADDITIONAL_REQUESTS_URL
    : MANAGE_BUDGET_URL
  const resetDate = formatQuotaResetLabel(e.quotaResetAt)

  switch (scenario) {
    case 'free-almost-out':
      return (
        <>
          You&apos;re almost out of your included usage with the free plan.
          {e.canUpgradePlan && (
            <>
              {' '}
              <UpgradeLink url={upgradeLinkUrl} />
              {upgradeSuffix}
            </>
          )}
        </>
      )
    case 'free-approaching':
      return (
        <>
          You&apos;re approaching the limit of your included usage with the free plan.
          {e.canUpgradePlan && (
            <>
              {' '}
              <UpgradeLink url={upgradeLinkUrl} />
              {upgradeSuffix}
            </>
          )}
        </>
      )
    case 'free-default':
      return e.canUpgradePlan ? (
        <>
          <UpgradeLink url={upgradeLinkUrl} /> to access more models and higher limits.
        </>
      ) : null
    case 'individual-overage-active':
    case 'pro-plus-overage-active':
    case 'max-overage-active':
      return (
        <>
          <span>
            {`You're now using additional usage until your included credits reset${resetDate ? ` ${resetDate}` : ''}.`}
          </span>{' '}
          <ManageBillingLink url={MANAGE_BUDGET_URL} />
        </>
      )
    case 'individual-default':
      return e.canUpgradePlan ? (
        <>
          <UpgradeLink url={upgradeLinkUrl} /> to access more models and higher limits.
        </>
      ) : null
    case 'individual-premium-approaching':
    case 'pro-plus-premium-approaching':
    case 'max-premium-approaching':
      return e.canUpgradePlan ? (
        <>
          You&apos;ve used {formatUsagePercent(e.premiumChatQuotaRemaining)}% of your included AI credits.{' '}
          <UpgradeLink url={upgradeLinkUrl} /> to increase your limit or{' '}
          <EnableAdditionalLink label="enable additional usage" url={enableAdditionalUrl} /> to get more.
        </>
      ) : (
        <>
          You&apos;ve used {formatUsagePercent(e.premiumChatQuotaRemaining)}% of your included AI credits.{' '}
          <EnableAdditionalLink label="Enable additional usage" url={enableAdditionalUrl} /> to get more.
        </>
      )
    case 'business-premium-approaching-upgradeable':
      return (
        <>
          You&apos;ve used {formatUsagePercent(e.premiumChatQuotaRemaining)}% of your included AI credits.{' '}
          <UpgradeLink url={upgradeLinkUrl} /> to increase your limit.
        </>
      )
    case 'business-premium-approaching-locked':
      return (
        <>
          You&apos;ve used {formatUsagePercent(e.premiumChatQuotaRemaining)}% of your included AI credits.{' '}
          <AdminBold label="Ask your admin to upgrade" /> to increase your limit.
        </>
      )
    case 'enterprise-premium-approaching-purchasable':
      return (
        <>
          You&apos;ve used {formatUsagePercent(e.premiumChatQuotaRemaining)}% of your included AI credits.{' '}
          <EnableAdditionalLink label="Enable additional usage" url={enableAdditionalUrl} /> to get more usage after the
          limit is reached.
        </>
      )
    case 'enterprise-premium-approaching-locked':
      return (
        <>
          You&apos;ve used {formatUsagePercent(e.premiumChatQuotaRemaining)}% of your included AI credits. Ask your
          admin to <AdminBold label="enable additional usage" /> to get more usage.
        </>
      )
    case null:
      return null
    default:
      // For UBB, we shouldn't have any 'exceeded' scenarios because the picker is disabled when over quota. If we do hit one of those scenarios, or any other unexpected scenario, log it and return null so the footer doesn't render at all.
      return null
  }
}

function renderLegacyScenario(scenario: QuotaScenario | null, e: EntitlementSnapshot): ReactNode {
  switch (scenario) {
    case 'free-exceeded':
      return e.canUpgradePlan ? (
        <>
          You have used all requests available this month. <UpgradeLink />.
        </>
      ) : (
        <>You have used all requests available this month.</>
      )
    // Legacy thresholds (LEGACY_THRESHOLDS) never produce 'free-almost-out';
    // included for exhaustive switch coverage only.
    case 'free-almost-out':
      return null
    case 'free-approaching':
      return e.canUpgradePlan ? (
        <>
          You have used {100 - FREE_QUOTA_TRIGGER}% of your free requests this month. <UpgradeLink /> to increase your
          limit.
        </>
      ) : (
        <>You have used {100 - FREE_QUOTA_TRIGGER}% of your free requests this month.</>
      )
    case 'free-default':
      return e.canUpgradePlan ? (
        <>
          <UpgradeLink /> to access more models and higher limits.
        </>
      ) : null
    case 'individual-overage-active':
    case 'pro-plus-overage-active':
      return (
        <>
          You have used all premium requests available this month. You will be charged per premium request until the
          limit resets on {e.resetDate}. <ManageBillingLink />
        </>
      )
    case 'individual-premium-exceeded':
      return e.canUpgradePlan ? (
        <>
          You have used all premium requests available this month. <UpgradeLink /> to increase your limit. Limit resets
          on {e.resetDate}.
        </>
      ) : (
        <>You have used all premium requests available this month. Limit resets on {e.resetDate}.</>
      )
    case 'individual-premium-approaching':
      return e.canUpgradePlan ? (
        <>
          You have used {100 - PREMIUM_QUOTA_TRIGGER}% of your premium requests this month. <UpgradeLink /> to increase
          your limit.
        </>
      ) : (
        <>You have used {100 - PREMIUM_QUOTA_TRIGGER}% of your premium requests this month.</>
      )
    case 'individual-default':
      return e.canUpgradePlan &&
        (copilotFeatureFlags.recommendedChatModelsOnly || copilotFeatureFlags.modelsViaBackend) ? (
        <>
          <UpgradeLink /> to access more models and higher limits.
        </>
      ) : null
    case 'pro-plus-premium-exceeded':
      return (
        <>
          You have used all premium requests available this month.{' '}
          <EnableAdditionalLink label="Enable additional requests" /> to get more. Limit resets on {e.resetDate}.
        </>
      )
    case 'pro-plus-premium-approaching':
      return (
        <>
          You have used {100 - PREMIUM_QUOTA_TRIGGER}% of your premium requests this month.{' '}
          <EnableAdditionalLink label="Enable additional requests" /> to get more.
        </>
      )
    case 'business-premium-exceeded-upgradeable':
      return (
        <>
          You have used all premium requests available this month. <UpgradeLink /> to increase your limit. Limit resets
          on {e.resetDate}.
        </>
      )
    case 'business-premium-approaching-upgradeable':
      return (
        <>
          You have used {100 - PREMIUM_QUOTA_TRIGGER}% of your premium requests this month. <UpgradeLink /> to increase
          your limit.
        </>
      )
    case 'business-premium-exceeded-locked':
      return (
        <>
          You have reached your monthly limit for premium requests. <AdminBold label="Ask your admin to upgrade" /> to
          increase your limit. Limit resets on {e.resetDate}.
        </>
      )
    case 'business-premium-approaching-locked':
      return (
        <>
          You have used {100 - PREMIUM_QUOTA_TRIGGER}% of your premium requests this month.{' '}
          <AdminBold label="Ask your admin to upgrade" /> to increase your limit.
        </>
      )
    case 'enterprise-premium-exceeded-purchasable':
      return (
        <>
          You have used all premium requests available this month.{' '}
          <EnableAdditionalLink label="Enable additional requests" /> to increase your limit. Limit resets on{' '}
          {e.resetDate}.
        </>
      )
    case 'enterprise-premium-exceeded-locked':
      return (
        <>
          You have reached your monthly limit for premium requests. Ask your admin to{' '}
          <AdminBold label="enable additional requests" /> to get more usage. Limit resets on {e.resetDate}.
        </>
      )
    case 'enterprise-premium-approaching-purchasable':
      return (
        <>
          You have used {100 - PREMIUM_QUOTA_TRIGGER}% of your premium requests this month.{' '}
          <EnableAdditionalLink label="Enable additional requests" /> to get more usage after the limit is reached.
        </>
      )
    case 'enterprise-premium-approaching-locked':
      return (
        <>
          You have used {100 - PREMIUM_QUOTA_TRIGGER}% of your premium requests this month. Ask your admin to{' '}
          <AdminBold label="enable additional requests" /> to get more usage.
        </>
      )
    case null:
      return null
  }
}
